import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useParams, Link, useNavigate } from 'react-router-dom';
import { Search, Filter, Package, X, Loader } from 'lucide-react';
import { slugify, buildSubcategorySlugMap, isLegacySubcategoryUrl } from '../utils/slugs';
import { useSeo } from '../seo/SeoProvider';
import {PriorityIntro, PriorityFamilyContent, PriorityLinks} from '../components/PriorityFamilyContent';
import {FamilyGuide, CategoryGuide} from '../components/FamilyGuide';
const {profiles, matchesPriority, bookingUrl} = require('../seo/priority');
const {productUrl} = require('../seo/registry');

// Mapping between URL slugs and category names
const categorySlugMap = {
  'evenementiel-barnum': 'Evenementiel - Barnum',
  'bricolage-travaux': 'Bricolage - Travaux',
  'jardinage-motoculture': 'Jardinage - Motoculture',
  'nettoyage-logement': 'Nettoyage - Logement',
};

// Reverse mapping for generating URLs
const categoryToSlug = {
  'Evenementiel - Barnum': 'evenementiel-barnum',
  'Bricolage - Travaux': 'bricolage-travaux',
  'Jardinage - Motoculture': 'jardinage-motoculture',
  'Nettoyage - Logement': 'nettoyage-logement',
};

// Convertit un nom de sous-catégorie en slug SEO-friendly
// "Location Bétonnière" -> "location-betonniere"
const subcategoryToSlug = (name) => slugify(name);

// Helper déprécié, conservé pour compatibilité (ancien format encodé)
// Retourne la version legacy-decoded (ancien format) pour le fallback
const legacySlugToSubcategory = (slug) => {
  try {
    return decodeURIComponent(slug.replace(/\+/g, ' '));
  } catch (e) {
    return slug;
  }
};

// Fonction pour normaliser le texte (retirer les accents)
const normalizeText = (text) => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const CategoriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug, subcategorySlug } = useParams();
  const priorityProfile = profiles[subcategorySlug]?.category === slug ? profiles[subcategorySlug] : null;
  const effectiveFamilySlug = priorityProfile?.sourceFamily || subcategorySlug;
  const navigate = useNavigate();
  const {products, loading, error, seo} = useSeo();
  const catalogProducts = useMemo(() => (products || []).filter(p => p.isAvailable !== false), [products]);

  // Build a slug -> subcategory name map from the loaded products (SEO-friendly)
  const subcategorySlugMap = useMemo(() => buildSubcategorySlugMap(catalogProducts), [catalogProducts]);
  // Initial filters derived from the URL so the first (and pre-rendered) render is already filtered.
  const urlSearch = searchParams.get('search');
  const [searchQuery, setSearchQuery] = useState(urlSearch || '');
  const [selectedCategory, setSelectedCategory] = useState(() => (!urlSearch && categorySlugMap[slug]) || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(() => (!urlSearch && categorySlugMap[slug] && subcategorySlug && subcategorySlugMap[effectiveFamilySlug]) || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Handle URL slug for category and subcategory
  useEffect(() => {
    const subcategoryFromUrl = searchParams.get('subcategory');
    const categoryFromUrl = searchParams.get('category');
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      setSelectedCategory('all');
      setSelectedSubcategory('all');
      return;
    }

    if (slug && categorySlugMap[slug]) {
      // We have a category slug in the URL
      setSelectedCategory(categorySlugMap[slug]);
      setSearchQuery('');
      
      // Check if there's a subcategory in the URL path
      if (subcategorySlug) {
        // 1) Nouveau format slugifié propre : "location-betonniere"
        if (subcategorySlugMap[effectiveFamilySlug]) {
          setSelectedSubcategory(subcategorySlugMap[effectiveFamilySlug]);
        } else if (isLegacySubcategoryUrl(subcategorySlug) && catalogProducts.length > 0) {
          // 2) Ancien format encodé (%20, +, accents) : redirection 301 vers nouveau slug
          const legacyName = legacySlugToSubcategory(subcategorySlug);
          const matched = catalogProducts.find(p => p.subcategory === legacyName);
          if (matched) {
            navigate(`/equipements/${slug}/${slugify(matched.subcategory)}`, { replace: true });
            return;
          }
          // Fallback : utiliser le nom décodé
          setSelectedSubcategory(legacyName);
        } else {
          // 3) Dernier recours : utiliser la valeur du slug telle quelle
          setSelectedSubcategory(subcategorySlug);
        }
      } else if (subcategoryFromUrl) {
        // Fallback to query param for backwards compatibility
        setSelectedSubcategory(subcategoryFromUrl);
      } else {
        setSelectedSubcategory('all');
      }
    } else if (!slug) {
      // Handle legacy query params (no slug)
      if (searchFromUrl) {
        setSearchQuery(searchFromUrl);
        setSelectedCategory('all');
        setSelectedSubcategory('all');
      } else if (categoryFromUrl) {
        setSelectedCategory(categoryFromUrl);
        setSelectedSubcategory('all');
        setSearchQuery('');
      } else if (subcategoryFromUrl) {
        setSelectedSubcategory(subcategoryFromUrl);
        setSelectedCategory('all');
        setSearchQuery('');
      } else {
        setSelectedCategory('all');
        setSelectedSubcategory('all');
        setSearchQuery('');
      }
    }
  }, [slug, subcategorySlug, effectiveFamilySlug, searchParams, subcategorySlugMap, catalogProducts, navigate]);

  // Update URL when category changes - navigate to the category page
  const handleCategoryChange = (value) => {
    if (value && value !== 'all') {
      const categorySlug = categoryToSlug[value];
      if (categorySlug) {
        navigate(`/equipements/${categorySlug}`);
      }
    } else {
      // Go to full catalog
      navigate('/categories');
    }
  };

  // Update URL when subcategory changes - navigate to the subcategory page
  const handleSubcategoryChange = (value) => {
    if (value && value !== 'all') {
      // Find which category this subcategory belongs to
      const product = catalogProducts.find(p => p.subcategory === value);
      if (product) {
        const categorySlug = categoryToSlug[product.category];
        const subcatSlug = subcategoryToSlug(value);
        navigate(`/equipements/${categorySlug}/${subcatSlug}`);
      }
    } else if (selectedCategory !== 'all') {
      // Keep current category but remove subcategory filter
      const categorySlug = categoryToSlug[selectedCategory];
      navigate(`/equipements/${categorySlug}`);
    } else {
      // Go to full catalog
      navigate('/categories');
    }
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    const params = new URLSearchParams(searchParams);
    if (value) params.set('search', value); else params.delete('search');
    setSearchParams(params, {replace:true});
  };

  const filteredProducts = useMemo(() => {
    let filtered = [...catalogProducts];

    // Search filter with accent normalization
    if (searchQuery) {
      const normalizedQuery = normalizeText(searchQuery);
      filtered = filtered.filter(p => 
        normalizeText(p.name).includes(normalizedQuery) ||
        normalizeText(p.subcategory).includes(normalizedQuery) ||
        normalizeText(p.category).includes(normalizedQuery)
      );
    }

    // Category filter (univers) - FIXED: now properly filters by main category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Subcategory filter
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    }
    if (priorityProfile && !searchQuery) filtered = filtered.filter(p => matchesPriority(p, priorityProfile));

    // Sort
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.priceWeekend - b.priceWeekend);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.priceWeekend - a.priceWeekend);
        break;
      case 'name':
      default:
        filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [catalogProducts, searchQuery, selectedCategory, selectedSubcategory, sortBy, priorityProfile]);

  const uniqueSubcategories = [...new Set(catalogProducts.map(p => p.subcategory).filter(s => s))].sort();

  // Group products by subcategory
  const groupedProducts = useMemo(() => {
    const groups = {};
    filteredProducts.forEach(product => {
      const cat = product.subcategory;
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(product);
    });
    return groups;
  }, [filteredProducts]);

  // Format price for display - now shows "XX€/jour - XX€/WE"
  // Si prix jour = 0, afficher uniquement le prix weekend
  const formatPriceDisplay = (product) => {
    if (product.isBarnumRedirect) {
      return { text: "Sur devis", subtext: "Barnum Événement", isDevis: true };
    }
    if (product.priceJour === "sur devis" || product.priceWeekend === "sur devis") {
      return { text: "Sur devis", subtext: "Nous contacter", isDevis: true };
    }
    if (product.priceJour === 0 && product.priceWeekend === 0) {
      return { text: "Nous consulter", subtext: "", isDevis: true };
    }
    // Si prix jour = 0, afficher uniquement le prix weekend
    if (product.priceJour === 0) {
      return { 
        text: `${product.priceWeekend}€/WE`, 
        subtext: "",
        isDevis: false 
      };
    }
    return { 
      text: `${product.priceJour}€/jour`, 
      subtext: `${product.priceWeekend}€/WE`,
      isDevis: false 
    };
  };

  const formatPrice = (price) => {
    if (typeof price === 'string') return price;
    return price > 0 ? `${price}€` : 'Sur devis';
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSortBy('name');
    // Navigate to full catalog
    navigate('/categories');
  };

  const hasActiveFilters = searchQuery || selectedSubcategory !== 'all' || selectedCategory !== 'all';

  // SEO: Schémas JSON-LD (Breadcrumbs + ItemList Products), rendus dans le HTML
  const catalogSchemas = useMemo(() => {
    if (!filteredProducts || filteredProducts.length === 0) return [];

    const baseUrl = process.env.REACT_APP_SITE_URL;
    const categoryName = selectedCategory !== 'all' ? selectedCategory : null;
    const subcategoryName = selectedSubcategory !== 'all' ? (priorityProfile?.label || selectedSubcategory) : null;

    const breadcrumbItems = [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Catalogue', item: `${baseUrl}/categories` }
    ];
    if (categoryName) {
      const catSlug = categoryToSlug[categoryName];
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: 3,
        name: categoryName,
        item: `${baseUrl}/equipements/${catSlug}`
      });
      if (subcategoryName) {
        breadcrumbItems.push({
          '@type': 'ListItem',
          position: 4,
          name: subcategoryName,
          item: `${baseUrl}${seo.path}`
        });
      }
    }

    return [
      {'@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: breadcrumbItems},
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: filteredProducts.slice(0, 50).map((product, idx) => ({
          '@type': 'ListItem', position: idx + 1, name: product.name, url: `${baseUrl}${productUrl(product)}`
        }))
      }
    ];
  }, [filteredProducts, selectedCategory, selectedSubcategory, priorityProfile, seo.path]);

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin w-12 h-12 text-green-500 mx-auto mb-4" />
          <p data-testid="catalog-loading" role="status" className="text-slate-600">Chargement des produits...</p>
        </div>
      </div>
    );
  }

  // Afficher une erreur si nécessaire
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p data-testid="catalog-error" role="alert" className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            data-testid="catalog-retry"
            className="px-4 py-2 bg-green-500 text-white rounded-lg"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-slate-50">
      {catalogSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}} />
      ))}
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 data-testid="catalog-heading" className="text-4xl sm:text-5xl font-black text-white mb-4 text-center break-words">
            {seo.h1}
          </h1>
          <p data-testid="catalog-result-count" className="text-xl text-slate-300 text-center mb-8">
            {filteredProducts.length} équipement{filteredProducts.length > 1 ? 's' : ''} disponible{filteredProducts.length > 1 ? 's' : ''} à la location
          </p>
          {priorityProfile && !searchQuery && <PriorityIntro profile={priorityProfile} />}

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={22} />
              <input
                type="text"
                data-testid="catalog-search"
                aria-label="Rechercher un équipement"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Rechercher un équipement..."
                className="w-full pl-12 pr-12 py-4 bg-white rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-green-500 shadow-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange('')}
                  data-testid="catalog-clear-search"
                  aria-label="Effacer la recherche"
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full"
                >
                  <X size={20} className="text-slate-400" />
                </button>
              )}
            </div>
            {(selectedSubcategory !== 'all' || selectedCategory !== 'all') && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <span className="text-slate-400">Filtre actif :</span>
                <span className="px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium flex items-center gap-2">
                  {selectedCategory !== 'all' ? selectedCategory : selectedSubcategory}
                  <button 
                    onClick={() => navigate('/categories')}
                    data-testid="catalog-remove-active-filter"
                    aria-label="Retirer le filtre"
                    className="hover:bg-green-600 rounded-full p-0.5"
                  >
                    <X size={14} />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {slug && subcategorySlug && !searchQuery && <PriorityLinks category={slug} sourceFamily={subcategorySlug} testId="catalog-family-shortcuts" />}

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters - Desktop */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-slate-800">Filtres</h3>
                  {hasActiveFilters && (
                    <button 
                      onClick={clearFilters}
                      data-testid="catalog-reset-desktop"
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>

                {/* Category Filter (Univers) */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Univers</label>
                  <select
                    value={selectedCategory}
                    data-testid="catalog-category-filter"
                    aria-label="Univers"
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      selectedCategory !== 'all' 
                        ? 'bg-green-50 border-green-300 text-green-700' 
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <option value="all">Tous les univers</option>
                    <option value="Evenementiel - Barnum">Evenementiel - Barnum</option>
                    <option value="Bricolage - Travaux">Bricolage - Travaux</option>
                    <option value="Jardinage - Motoculture">Jardinage - Motoculture</option>
                    <option value="Nettoyage - Logement">Nettoyage - Logement</option>
                  </select>
                </div>

                {/* Subcategory Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Sous-catégorie</label>
                  <select
                    value={selectedSubcategory}
                    data-testid="catalog-subcategory-filter"
                    aria-label="Sous-catégorie"
                    onChange={(e) => handleSubcategoryChange(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      selectedSubcategory !== 'all' 
                        ? 'bg-green-50 border-green-300 text-green-700' 
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <option value="all">Toutes les sous-catégories</option>
                    {(selectedCategory !== 'all' 
                      ? uniqueSubcategories.filter(sub => 
                          catalogProducts.some(p => p.category === selectedCategory && p.subcategory === sub)
                        )
                      : uniqueSubcategories
                    ).map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-3">Trier par</label>
                  <select
                    value={sortBy}
                    data-testid="catalog-sort-desktop"
                    aria-label="Trier les équipements"
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="name">Nom (A-Z)</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                  </select>
                </div>
              </div>
            </aside>

            {/* Mobile Filter Button */}
            <div className="lg:hidden flex gap-4 mb-4">
              <button
                onClick={() => setIsFilterOpen(true)}
                data-testid="catalog-open-mobile-filters"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl shadow-sm font-medium ${
                  hasActiveFilters 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white text-slate-700'
                }`}
              >
                <Filter size={20} />
                Filtres {hasActiveFilters && '•'}
              </button>
              <select
                value={sortBy}
                data-testid="catalog-sort-mobile"
                aria-label="Trier les équipements"
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-4 py-3 bg-white rounded-xl shadow-sm font-medium text-slate-700"
              >
                <option value="name">Nom (A-Z)</option>
                <option value="price-asc">Prix ↑</option>
                <option value="price-desc">Prix ↓</option>
              </select>
            </div>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Results count */}
              <div className="flex items-center justify-between mb-6">
                <p data-testid="catalog-filtered-count" className="text-slate-600">
                  <span className="font-bold text-slate-800">{filteredProducts.length}</span> résultat{filteredProducts.length > 1 ? 's' : ''}
                  {selectedCategory !== 'all' && (
                    <span className="text-green-600"> dans "{selectedCategory}"</span>
                  )}
                  {selectedSubcategory !== 'all' && (
                    <span className="text-green-600"> dans "{priorityProfile?.label || selectedSubcategory}"</span>
                  )}
                </p>
                {hasActiveFilters && (
                  <button 
                    onClick={clearFilters}
                    data-testid="catalog-reset-mobile"
                    className="lg:hidden flex items-center gap-1 text-sm text-green-600 font-medium"
                  >
                    <X size={16} /> Effacer filtres
                  </button>
                )}
              </div>

              {filteredProducts.length > 0 ? (
                <div className="space-y-12">
                  {Object.entries(groupedProducts).sort(([a], [b]) => a.localeCompare(b)).map(([category, products]) => (
                    <div key={category}>
                      {/* Category Header */}
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                          <Package className="text-white" size={24} />
                        </div>
                        <div>
                          <h2 data-testid={`catalog-group-${slugify(category)}`} className="text-2xl font-bold text-slate-800">{priorityProfile && !searchQuery ? priorityProfile.label : category}</h2>
                          <p className="text-slate-500 text-sm">{products.length} produit{products.length > 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      
                      {/* Products Grid */}
                      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {products.map((product) => (
                          <div
                            key={product.id}
                            data-testid={`catalog-product-${product.id}`}
                            className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
                          >
                            {/* Product Image */}
                            {product.image ? (
                              <div className="h-48 overflow-hidden bg-slate-50 flex items-center justify-center">
                                <img 
                                  src={product.image} 
                                  data-testid={`product-image-${product.id}`}
                                  alt={`${product.name} à louer chez 3DLoc`}
                                  loading="lazy"
                                  className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className="hidden w-full h-full items-center justify-center bg-slate-100">
                                  <Package className="text-slate-300" size={48} />
                                </div>
                              </div>
                            ) : (
                              <div className="h-48 bg-slate-100 flex items-center justify-center">
                                <Package className="text-slate-300" size={48} />
                              </div>
                            )}

                            <div className="p-5">
                              {/* Content */}
                              <h3 data-testid={`product-name-${product.id}`} className="font-bold text-slate-800 mb-2 line-clamp-2">
                                <Link to={productUrl(product)} data-testid={`product-link-${product.id}`} className="hover:text-green-600 transition-colors">
                                  {product.name}
                                </Link>
                              </h3>

                              {product.description && (
                                <p data-testid={`product-description-${product.id}`} className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">
                                  {product.description}
                                </p>
                              )}

                              {/* Details */}
                              <div className="space-y-2 text-sm">
                                {product.quantity > 1 && (
                                  <div data-testid={`product-quantity-${product.id}`} className="flex justify-between text-slate-500">
                                    <span>Disponible</span>
                                    <span className="font-medium text-slate-700">{product.quantity} unités</span>
                                  </div>
                                )}
                              </div>

                              {/* Price - New format: XX€/jour - XX€/WE */}
                              <div data-testid={`product-price-${product.id}`} className="mt-4 pt-4 border-t border-slate-100">
                                {(() => {
                                  const priceInfo = formatPriceDisplay(product);
                                  return (
                                    <div className="flex flex-wrap gap-3 items-end justify-between">
                                      <div>
                                        {priceInfo.isDevis ? (
                                          <>
                                            <p className="text-lg font-bold text-orange-500">{priceInfo.text}</p>
                                            {priceInfo.subtext && (
                                              <p className="text-xs text-slate-500">{priceInfo.subtext}</p>
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            <p className="text-xs text-slate-500 mb-1">À partir de:</p>
                                            <p className="text-xl font-black text-green-500">{priceInfo.text}</p>
                                            <p className="text-sm font-semibold text-green-600">{priceInfo.subtext}</p>
                                          </>
                                        )}
                                      </div>
                                      {product.isBarnumRedirect ? (
                                        <a
                                          href="https://www.barnum-evenement.fr"
                                          data-testid={`product-barnum-${product.id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors"
                                        >
                                          Barnum Événement
                                        </a>
                                      ) : bookingUrl(product) ? (
                                        <div className="text-right">
                                          <a
                                            href={bookingUrl(product)}
                                            data-testid={`product-book-${product.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors inline-block ${
                                              product.category === 'Evenementiel - Barnum' 
                                                ? 'bg-orange-500 hover:bg-orange-600' 
                                                : 'bg-green-500 hover:bg-green-600'
                                            }`}
                                          >
                                            {priorityProfile && !searchQuery ? 'Réservez 24h/24' : 'Réserver'}
                                          </a>
                                          {product.category === 'Evenementiel - Barnum' && (
                                            <p className="text-xs text-orange-600 mt-1">avec Barnum Événement</p>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-right">
                                          <Link
                                            to={`/contact?materiel=${encodeURIComponent(product.name)}`}
                                            data-testid={`product-quote-${product.id}`}
                                            className={`px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors inline-block ${
                                              product.category === 'Evenementiel - Barnum' 
                                                ? 'bg-orange-500 hover:bg-orange-600' 
                                                : 'bg-green-500 hover:bg-green-600'
                                            }`}
                                          >
                                            {product.category === 'Evenementiel - Barnum' ? 'Réserver' : 'Devis'}
                                          </Link>
                                          {product.category === 'Evenementiel - Barnum' && (
                                            <p className="text-xs text-orange-600 mt-1">avec Barnum Événement</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Package className="mx-auto text-slate-300 mb-4" size={64} />
                  <h3 data-testid="catalog-no-results" className="text-xl font-bold text-slate-800 mb-2">Aucun résultat</h3>
                  <p className="text-slate-500 mb-4">Essayez de modifier vos critères de recherche</p>
                  <button
                    onClick={clearFilters}
                    data-testid="catalog-reset-empty"
                    className="px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {priorityProfile && !searchQuery && <PriorityFamilyContent familySlug={subcategorySlug} />}
      {!priorityProfile && slug && subcategorySlug && !searchQuery && <FamilyGuide category={slug} familySlug={subcategorySlug} />}
      {slug && !subcategorySlug && !searchQuery && <CategoryGuide slug={slug} />}

      {/* Mobile Filter Modal */}
      {isFilterOpen && (
        <div data-testid="catalog-mobile-filters" role="dialog" aria-modal="true" aria-labelledby="catalog-filters-title" className="fixed inset-0 z-50 lg:hidden">
          <button data-testid="catalog-mobile-filter-overlay" aria-label="Fermer les filtres" className="absolute inset-0 bg-black/50" onClick={() => setIsFilterOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 id="catalog-filters-title" className="text-xl font-bold text-slate-800">Filtres</h3>
              <button data-testid="catalog-close-mobile-filters" aria-label="Fermer les filtres" onClick={() => setIsFilterOpen(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Catégorie</label>
                <select
                  value={selectedSubcategory}
                  data-testid="catalog-mobile-subcategory"
                  aria-label="Catégorie de matériel"
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl ${
                    selectedSubcategory !== 'all' 
                      ? 'bg-green-50 border-green-300 text-green-700' 
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <option value="all">Toutes les catégories</option>
                  {uniqueSubcategories.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => setIsFilterOpen(false)}
                data-testid="catalog-apply-mobile-filters"
                className="w-full py-4 bg-green-500 text-white font-bold rounded-xl"
              >
                Voir {filteredProducts.length} résultat{filteredProducts.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;
